import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PerformanceMetrics = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    averageBookingValue: 0,
    revenueGrowth: 0
  });
  const [bookingTrends, setBookingTrends] = useState([]);
  const [revenueByType, setRevenueByType] = useState([]);

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);

      // Fetch all bookings
      const { data: bookings, error } = await supabase
        ?.from('bookings')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate metrics
      const totalRevenue = bookings?.reduce((sum, b) => sum + parseFloat(b?.amount || 0), 0) || 0;
      const totalBookings = bookings?.length || 0;
      const averageBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;

      // Calculate revenue growth (last 30 days vs previous 30 days)
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

      const recentRevenue = bookings
        ?.filter(b => new Date(b?.created_at) >= thirtyDaysAgo)
        ?.reduce((sum, b) => sum + parseFloat(b?.amount || 0), 0) || 0;

      const previousRevenue = bookings
        ?.filter(b => new Date(b?.created_at) >= sixtyDaysAgo && new Date(b?.created_at) < thirtyDaysAgo)
        ?.reduce((sum, b) => sum + parseFloat(b?.amount || 0), 0) || 0;

      const revenueGrowth = previousRevenue > 0 ? ((recentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

      setMetrics({
        totalRevenue,
        totalBookings,
        averageBookingValue,
        revenueGrowth
      });

      // Generate booking trends (last 7 days)
      const trendData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = date?.toISOString()?.split('T')?.[0];
        const dayBookings = bookings?.filter(b => b?.created_at?.startsWith(dateStr)) || [];
        
        trendData?.push({
          date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          bookings: dayBookings?.length,
          revenue: dayBookings?.reduce((sum, b) => sum + parseFloat(b?.amount || 0), 0)
        });
      }
      setBookingTrends(trendData);

      // Revenue by booking type
      const typeRevenue = {};
      bookings?.forEach(b => {
        const type = b?.type || 'other';
        typeRevenue[type] = (typeRevenue?.[type] || 0) + parseFloat(b?.amount || 0);
      });

      const revenueData = Object?.entries(typeRevenue)?.map(([type, revenue]) => ({
        type: type?.charAt(0)?.toUpperCase() + type?.slice(1),
        revenue: revenue
      }));
      setRevenueByType(revenueData);

    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={24} />
            <span className="text-teal-100 text-sm">Total Revenue</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            ₱{metrics?.totalRevenue?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="flex items-center text-sm text-teal-100">
            <Icon name={metrics?.revenueGrowth >= 0 ? 'TrendingUp' : 'TrendingDown'} size={16} className="mr-1" />
            {Math?.abs(metrics?.revenueGrowth)?.toFixed(1)}% vs last month
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Calendar" size={24} />
            <span className="text-blue-100 text-sm">Total Bookings</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {metrics?.totalBookings?.toLocaleString()}
          </div>
          <div className="text-sm text-blue-100">All time bookings</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingUp" size={24} />
            <span className="text-purple-100 text-sm">Avg Booking Value</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            ₱{metrics?.averageBookingValue?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-sm text-purple-100">Per transaction</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Activity" size={24} />
            <span className="text-orange-100 text-sm">Growth Rate</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {metrics?.revenueGrowth >= 0 ? '+' : ''}{metrics?.revenueGrowth?.toFixed(1)}%
          </div>
          <div className="text-sm text-orange-100">Last 30 days</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Booking Trends */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="TrendingUp" size={20} className="mr-2 text-teal-600" />
            Booking Trends (Last 7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={bookingTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="bookings" stroke="#0d9488" strokeWidth={2} name="Bookings" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Type */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="BarChart3" size={20} className="mr-2 text-blue-600" />
            Revenue by Booking Type
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueByType}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis />
              <Tooltip formatter={(value) => `₱${value?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`} />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue (₱)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;